import axios, { AxiosResponse } from 'axios';
import qs from 'qs';
import ApiClientManager from 'core/apiClient';
import { reduxHelper } from 'src';

import { AppRequestStatusManager } from 'RequestStatusModule/RequestStatusManager/RequestStatusManager';
import { handleApiError } from './apiErrorHandler';
import { openUnauthorizedModal } from 'actionCreators';
import { accessTokenManager } from 'core/accessTokenManager';
import { handleRefreshedToken } from './handleRefreshedToken';

// Note: ApiConfig type is globally available from @types/global.d.ts

/**
 * Resolves the auth token from the right source.
 * Prefers accessTokenManager (fresh from silent refresh) over the Redux-passed fallback.
 *
 * Returns fallbackToken as-is when:
 * - fallbackToken is empty — caller intentionally wants no auth (e.g. signIn, presigned uploads)
 * - skipResolution is true — caller passes a non-JWT credential (e.g. temp token exchange, impersonation)
 */
const resolveAuthToken = (fallbackToken: string, skipResolution?: boolean): string => {
  if (!fallbackToken || skipResolution) return fallbackToken;
  return accessTokenManager.getAccessToken() || fallbackToken;
};

const myAxios = axios.create({
  validateStatus: (status) => status < 500,
});

/**
 * Serializes query parameters for API requests
 *
 * @param params - Query parameters object
 * @returns Serialized query string
 */
const paramsSerializer = (params: Record<string, unknown>) => {
  return qs.stringify(params, {
    arrayFormat: 'brackets',
  });
};

const destructureConfig = (custom_config: ApiConfig = {}, axios_config: ApiConfig = {}) => {
  return {
    ...axios_config,
    ...custom_config,
    headers: {
      ...axios_config?.headers,
      ...custom_config?.headers,
    },
  };
};

/**
 * Handles API response based on HTTP status code
 *
 * @param response - Axios response object
 * @param resolve - Promise resolve function
 * @param reject - Promise reject function
 */
const handleResponse = <T = unknown>(
  response: AxiosResponse<T>,
  resolve: (value: AxiosResponse<T>) => void,
  reject: (reason: AxiosResponse<T>) => void,
) => {
  if (response.status >= 400) {
    reject(response);
  } else {
    resolve(response);
  }
};

/**
 * Creates request handlers with pre-configured settings
 *
 * @param configBuilder - Function that returns API configuration
 * @returns Object with HTTP method handlers
 */
const makeRequestHandlersWithConfigs = ({ configBuilder }: { configBuilder: () => ApiConfig }) => {
  const get = <T = unknown>(
    url: string,
    config: ApiConfig,
    token = '',
    skipTokenResolution = false,
  ) => getAPI<T>(url, destructureConfig(config, configBuilder()), token, skipTokenResolution);

  const post = <TRequest = unknown, TResponse = unknown>(
    url: string,
    data: TRequest,
    config: ApiConfig,
    token = '',
  ) => postAPI<TRequest, TResponse>(url, data, destructureConfig(config, configBuilder()), token);

  const put = <TRequest = unknown, TResponse = unknown>(
    url: string,
    data: TRequest,
    config: ApiConfig,
    token = '',
  ) => putAPI<TRequest, TResponse>(url, data, destructureConfig(config, configBuilder()), token);

  const patch = <TRequest = unknown, TResponse = unknown>(
    url: string,
    data: TRequest,
    config: ApiConfig,
    token = '',
  ) => patchAPI<TRequest, TResponse>(url, data, destructureConfig(config, configBuilder()), token);

  // delete is a reserved keyword
  const _delete = <TRequest = unknown, TResponse = unknown>(
    url: string,
    data: TRequest,
    config: ApiConfig,
    token = '',
  ) => deleteAPI<TRequest, TResponse>(url, data, destructureConfig(config, configBuilder()), token);

  return {
    get,
    post,
    put,
    patch,
    delete: _delete,
  };
};

myAxios.defaults.paramsSerializer = paramsSerializer;

// Helper to open global modal on 401 Unauthorized
const openUnauthorizedIfNeeded = (status?: number) => {
  if (status === 401) {
    const state = reduxHelper.getState();
    const isOpen = state?.globalReducer?.isUnauthorizedModalOpen;
    if (!isOpen) {
      reduxHelper.getDispatch()(openUnauthorizedModal());
    }
  }
};

// Handle API errors with smart notifications and 401 unauthorized modal
myAxios.interceptors.response.use(
  (response) => {
    try {
      handleRefreshedToken(response);
      openUnauthorizedIfNeeded(response?.status);
    } catch {
      // no-op: avoid breaking response flow on any unexpected error
    }
    return response;
  },
  (error) => {
    try {
      if (error?.response) handleRefreshedToken(error.response);
      openUnauthorizedIfNeeded(error?.response?.status);
      // Handle all errors with smart toast notifications
      handleApiError(error);
    } catch (e) {
      // no-op: avoid breaking response flow on any unexpected error
      console.error('Error in API error handler:', e);
    }
    return Promise.reject(error);
  },
);

/**
 * Builds abort signal for request cancellation
 *
 * @param config - API configuration containing optional requestStatusId
 * @returns Object with abort signal if request tracking is enabled
 */
const buildSignalForRequest = ({ config }: { config: ApiConfig }) => {
  const result: {
    signal?: AbortController['signal'];
  } = {};

  if (!config) {
    return result;
  }

  const requestStatusId: string | undefined =
    'requestStatusId' in config ? String(config.requestStatusId) : undefined;

  if (!requestStatusId) {
    return result;
  }

  // Abort controller should already be created in request status middleware
  const abortController = AppRequestStatusManager.getRequestStatusAbortController({
    requestStatusId,
  });
  if (abortController) {
    result.signal = abortController.signal;
  }

  return result;
};

/**
 * Performs HTTP GET request with type safety
 *
 * @template T - Response data type
 * @param url - API endpoint URL
 * @param config - Optional request configuration
 * @param token - Authentication token
 * @returns Promise resolving to typed Axios response
 *
 * @example
 * ```ts
 * interface User { id: string; name: string; }
 * const response = await getAPI<User[]>('/users', { params: { page: 1 } }, token);
 * console.log(response.data); // Type: User[]
 * ```
 */
export const getAPI = <T = unknown>(
  url: string,
  config: ApiConfig = {},
  token = '',
  skipTokenResolution = false,
): Promise<AxiosResponse<T>> => {
  return new Promise<AxiosResponse<T>>((resolve, reject) => {
    myAxios
      .get<T>(url, {
        ...config,
        ...buildSignalForRequest({ config }),
        headers: {
          Authorization: `Bearer ${resolveAuthToken(token, skipTokenResolution)}`,
          ...config?.headers,
        },
      })
      .then((response) => handleResponse<T>(response, resolve, reject))
      .catch((error: unknown) => {
        reject(handleError(error));
      });
  });
};

/**
 * Performs HTTP POST request with type safety
 *
 * @template TRequest - Request body type
 * @template TResponse - Response data type
 * @param url - API endpoint URL
 * @param data - Request payload
 * @param config - Optional request configuration
 * @param token - Authentication token
 * @returns Promise resolving to typed Axios response
 *
 * @example
 * ```ts
 * interface CreateUserRequest { name: string; email: string; }
 * interface CreateUserResponse { id: string; name: string; email: string; }
 * const response = await postAPI<CreateUserRequest, CreateUserResponse>(
 *   '/users',
 *   { name: 'John', email: 'john@example.com' },
 *   {},
 *   token
 * );
 * ```
 */
export const postAPI = <TRequest = unknown, TResponse = unknown>(
  url: string,
  data: TRequest,
  config: ApiConfig,
  token = '',
): Promise<AxiosResponse<TResponse>> => {
  return new Promise<AxiosResponse<TResponse>>((resolve, reject) => {
    myAxios
      .post<TResponse>(url, data, {
        transformRequest: function (data: TRequest) {
          return JSON.stringify(data);
        },
        ...config,
        ...buildSignalForRequest({ config }),
        headers: {
          Authorization: `Bearer ${resolveAuthToken(token)}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...config?.headers,
        },
      })
      .then((response) => handleResponse<TResponse>(response, resolve, reject))
      .catch((error: unknown) => reject(handleError(error)));
  });
};

/**
 * Performs HTTP PUT request with type safety
 *
 * @template TRequest - Request body type
 * @template TResponse - Response data type
 */
export const putAPI = <TRequest = unknown, TResponse = unknown>(
  url: string,
  data: TRequest,
  config: ApiConfig,
  token = '',
): Promise<AxiosResponse<TResponse>> => {
  return new Promise<AxiosResponse<TResponse>>((resolve, reject) => {
    myAxios
      .put<TResponse>(url, data, {
        transformRequest: function (data: TRequest) {
          return JSON.stringify(data);
        },
        ...config,
        ...buildSignalForRequest({ config }),
        headers: {
          Authorization: `Bearer ${resolveAuthToken(token)}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...config?.headers,
        },
      })
      .then((response) => handleResponse<TResponse>(response, resolve, reject))
      .catch((error: unknown) => reject(handleError(error)));
  });
};

/**
 * Performs HTTP PATCH request with type safety
 *
 * @template TRequest - Request body type
 * @template TResponse - Response data type
 */
export const patchAPI = <TRequest = unknown, TResponse = unknown>(
  url: string,
  data: TRequest,
  config: ApiConfig,
  token = '',
): Promise<AxiosResponse<TResponse>> => {
  return new Promise<AxiosResponse<TResponse>>((resolve, reject) => {
    myAxios
      .patch<TResponse>(url, data, {
        transformRequest: function (data: TRequest) {
          return JSON.stringify(data);
        },
        ...config,
        ...buildSignalForRequest({ config }),
        headers: {
          Authorization: `Bearer ${resolveAuthToken(token)}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...config?.headers,
        },
      })
      .then((response) => handleResponse<TResponse>(response, resolve, reject))
      .catch((error: unknown) => reject(handleError(error)));
  });
};

/**
 * Performs HTTP DELETE request with type safety
 *
 * @template TRequest - Request body type (optional)
 * @template TResponse - Response data type
 */
export const deleteAPI = <TRequest = unknown, TResponse = unknown>(
  url: string,
  data: TRequest,
  config: ApiConfig,
  token = '',
): Promise<AxiosResponse<TResponse>> => {
  return new Promise<AxiosResponse<TResponse>>((resolve, reject) => {
    myAxios
      .delete<TResponse>(url, {
        data,
        transformRequest: function (data: TRequest) {
          return JSON.stringify(data);
        },
        ...config,
        ...buildSignalForRequest({ config }),
        headers: {
          Authorization: `Bearer ${resolveAuthToken(token)}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...config?.headers,
        },
      })
      .then((response) => handleResponse<TResponse>(response, resolve, reject))
      .catch((error: unknown) => reject(handleError(error)));
  });
};

/* ----------------------- Rails Server config ---------------------- */
export const {
  get: getApiRailsServer,
  post: postApiRailsServer,
  put: putApiRailsServer,
  delete: deleteApiRailsServer,
} = makeRequestHandlersWithConfigs({
  configBuilder: () => ApiClientManager.buildRailsServerApiConfigs(),
});

/* ----------------------- Integration Server config ---------------------- */
export const {
  get: getApiIntegrationServer,
  post: postApiIntegrationServer,
  put: putApiIntegrationSever,
  delete: deleteApiIntegrationSever,
} = makeRequestHandlersWithConfigs({
  configBuilder: () => ApiClientManager.buildIntegrationServerApiConfigs(),
});

/* ------------------------------- Galaxy Server config ------------------------------ */

export const {
  get: getApiGalaxyServer,
  post: postApiGalaxyServer,
  put: putApiGalaxyServer,
  patch: patchApiGalaxyServer,
  delete: deleteApiGalaxyServer,
} = makeRequestHandlersWithConfigs({
  configBuilder: () => ({
    baseURL: ApiClientManager.getGalaxyServerUrl(),
    headers: ApiClientManager.buildUrlHeaders(),
    withCredentials: true,
  }),
});

const ERRORS_CODE_MAPPING = {
  403: "You are not authorized to access team's resources. Please log out and sign-in using another account",
  404: 'Resources not found',
};

/**
 * API Error type with standardized properties
 */
export interface ApiError extends Error {
  /** HTTP status code */
  status?: number;
  /** Developer-friendly error message for debugging */
  devMessage?: string;
  /** Response data from server */
  data?: unknown;
  /** Error type identifier for reducers */
  type?: string;
  /** Axios error flag */
  isAxiosError?: boolean;
  /** Original axios config */
  config?: {
    url?: string;
    method?: string;
    [key: string]: unknown;
  };
  /** Response object */
  response?: {
    status?: number;
    data?: unknown;
    [key: string]: unknown;
  };
}

/**
 * General error handler - standardizes errors for consistent handling
 *
 * Transforms errors into a standardized format with:
 * - message: User-friendly error message
 * - name: Error name (from Error object)
 * - status: HTTP status code
 * - devMessage: Developer-friendly debugging info
 * - type: Error type identifier for reducers
 *
 * @param error - Error object (usually from Axios)
 * @returns Standardized error object
 */
export const handleError = (error: unknown): ApiError => {
  // Type guard for axios-like errors
  const isAxiosError = (err: unknown): err is ApiError => {
    return (
      typeof err === 'object' &&
      err !== null &&
      'isAxiosError' in err &&
      Boolean((err as ApiError).isAxiosError)
    );
  };

  // Create error object
  const errorToUse: ApiError =
    error instanceof Error
      ? { ...error, name: error.name, message: error.message }
      : { name: 'UnknownError', message: 'An unknown error occurred' };

  /** Generalize axios error */
  if (isAxiosError(error)) {
    const axiosError = error as ApiError;
    const status = axiosError.response?.status;

    errorToUse.message =
      (status && ERRORS_CODE_MAPPING[status as keyof typeof ERRORS_CODE_MAPPING]) ||
      'Failed to retrieve data. Please try again';
    errorToUse.devMessage = `Failed to fetch data on endpoint ${axiosError.config?.url}. Error code: ${status}`;
    errorToUse.status = status;
    errorToUse.data = axiosError.response?.data;
    errorToUse.isAxiosError = true;
    errorToUse.config = axiosError.config;
    errorToUse.response = axiosError.response;
  }

  /** Pass error to thunk */
  return errorToUse;
};
